import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { documentsAPI, documentTypesAPI, complianceAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  Upload,
  Download,
  Trash2,
  RefreshCw,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const STATUS_STYLES = {
  pending: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/20',
  approved: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
  rejected: 'bg-red-500/15 text-red-400 border-red-500/20',
  expired: 'bg-red-500/15 text-red-400 border-red-500/20',
};

const ACTIVATION_STATUS_STYLES = {
  pending: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/20',
  active: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
  inactive: 'bg-red-500/15 text-red-400 border-red-500/20',
};

export default function MedicDocuments() {
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statusRes, typesRes] = await Promise.allSettled([
        complianceAPI.getMyStatus(),
        documentTypesAPI.list()
      ]);

      let status = null;
      if (statusRes.status === 'fulfilled') {
        status = statusRes.value.data;
        setOnboardingStatus(status);
      } else {
        console.warn('compliance/my-status failed:', statusRes.reason?.response?.status);
        setOnboardingStatus(null);
      }

      if (typesRes.status === 'fulfilled') {
        setDocumentTypes(typesRes.value.data);
      } else {
        console.error('document-types failed:', typesRes.reason);
        toast.error('Failed to load document types');
      }

      if (status?.medic_id) {
        try {
          const docsRes = await documentsAPI.getByMedic(status.medic_id);
          setDocuments(docsRes.data);
        } catch (e) {
          console.warn('documents fetch failed:', e);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxSize: 10 * 1024 * 1024,
    multiple: false
  });

  const handleUpload = async () => {
    if (!selectedFile || !selectedType) {
      toast.error('Please select a file and document type');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('medic_id', onboardingStatus.medic_id);
      formData.append('document_type_id', selectedType);
      if (expiryDate) formData.append('expires_at', expiryDate);
      if (notes) formData.append('notes', notes);

      await documentsAPI.upload(formData);
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setSelectedType('');
      setExpiryDate('');
      setNotes('');
      fetchData();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await documentsAPI.delete(docId);
      toast.success('Document deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await documentsAPI.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const getDefaultExpiry = (typeId) => {
    const type = documentTypes.find(t => t.id === typeId);
    if (type?.default_expiry_months) {
      const date = new Date();
      date.setMonth(date.getMonth() + type.default_expiry_months);
      return date.toISOString().split('T')[0];
    }
    return '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1ABC9C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="medic-documents-container">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[#F2F2F2]">My Documents & Compliance</h1>
        <div className="flex items-center gap-2">
          <Button onClick={fetchData} variant="outline" className="border-white/10">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90">
                <Upload className="w-4 h-4 mr-2" />
                Upload Document
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#2B2B2B] border-white/10">
              <DialogHeader>
                <DialogTitle className="text-[#F2F2F2]">Upload Document</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-[#A1A1AA]">Document Type</Label>
                  <Select 
                    value={selectedType} 
                    onValueChange={(val) => {
                      setSelectedType(val);
                      setExpiryDate(getDefaultExpiry(val));
                    }}
                  >
                    <SelectTrigger className="bg-[#1E1E1E] border-white/10 text-[#F2F2F2]">
                      <SelectValue placeholder="Select document type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2B2B2B] border-white/10">
                      {documentTypes.map(type => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                          {type.required_for_activation && (
                            <span className="text-red-400 ml-1">*</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-[#A1A1AA]">Expiry Date</Label>
                  <Input
                    type="date"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                    className="bg-[#1E1E1E] border-white/10 text-[#F2F2F2]"
                  />
                </div>

                <div>
                  <Label className="text-[#A1A1AA]">Notes (Optional)</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any additional notes..."
                    className="bg-[#1E1E1E] border-white/10 text-[#F2F2F2]"
                  />
                </div>

                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    isDragActive 
                      ? 'border-[#1ABC9C] bg-[#1ABC9C]/10' 
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <input {...getInputProps()} />
                  <Upload className="w-8 h-8 mx-auto text-[#A1A1AA] mb-2" />
                  {selectedFile ? (
                    <p className="text-[#1ABC9C]">{selectedFile.name}</p>
                  ) : isDragActive ? (
                    <p className="text-[#1ABC9C]">Drop the file here...</p>
                  ) : (
                    <div>
                      <p className="text-[#F2F2F2]">Drag & drop a file here</p>
                      <p className="text-sm text-[#A1A1AA] mt-1">or click to select</p>
                      <p className="text-xs text-[#A1A1AA] mt-2">PDF, JPEG, PNG, DOC or DOCX (max 10MB)</p>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 justify-end">
                  <Button 
                    variant="outline" 
                    onClick={() => setUploadDialogOpen(false)}
                    className="border-white/10"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleUpload} 
                    disabled={uploading || !selectedFile || !selectedType}
                    className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
                  >
                    {uploading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Onboarding Status Card */}
      {onboardingStatus && (
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-[#F2F2F2]">Onboarding Status</CardTitle>
              <Badge className={ACTIVATION_STATUS_STYLES[onboardingStatus.status]}>
                {onboardingStatus.status.charAt(0).toUpperCase() + onboardingStatus.status.slice(1)}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-[#A1A1AA]">Compliance Progress</span>
                <span className="text-sm font-medium text-[#F2F2F2]">
                  {onboardingStatus.compliance_percentage}%
                </span>
              </div>
              <Progress value={onboardingStatus.compliance_percentage} className="h-2" />
            </div>

            {onboardingStatus.blockers?.length > 0 && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <div className="flex items-center gap-2 text-red-400 mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-medium">Action Required</span>
                </div>
                <ul className="list-disc list-inside text-sm text-[#A1A1AA] space-y-1">
                  {onboardingStatus.blockers.map((blocker, i) => (
                    <li key={i}>{blocker}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 rounded-lg bg-[#1E1E1E]">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#1ABC9C]" />
                  <span className="text-sm text-[#A1A1AA]">Required</span>
                </div>
                <p className="text-xl font-bold text-[#F2F2F2] mt-1">
                  {onboardingStatus.required_documents?.length || 0}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-[#1E1E1E]">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#1ABC9C]" />
                  <span className="text-sm text-[#A1A1AA]">Submitted</span>
                </div>
                <p className="text-xl font-bold text-[#F2F2F2] mt-1">
                  {onboardingStatus.submitted_documents?.length || 0}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-[#1E1E1E]">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm text-[#A1A1AA]">Pending</span>
                </div>
                <p className="text-xl font-bold text-yellow-400 mt-1">
                  {onboardingStatus.pending_approval_documents?.length || 0}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-[#1E1E1E]">
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-400" />
                  <span className="text-sm text-[#A1A1AA]">Missing</span>
                </div>
                <p className="text-xl font-bold text-red-400 mt-1">
                  {onboardingStatus.missing_documents?.length || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Required Documents */}
      <Card className="bg-[#2B2B2B] border-white/5">
        <CardHeader>
          <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#1ABC9C]" />
            Required Documents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {onboardingStatus?.required_documents?.map(reqDoc => {
              const submitted = documents.find(d => d.document_type_id === reqDoc.id);
              const isMissing = onboardingStatus.missing_documents?.some(m => m.id === reqDoc.id);
              
              return (
                <div 
                  key={reqDoc.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-[#1E1E1E]"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isMissing 
                        ? 'bg-red-500/20' 
                        : submitted?.status === 'approved' 
                          ? 'bg-[#1ABC9C]/20' 
                          : 'bg-yellow-500/20'
                    }`}>
                      <FileText className={`w-5 h-5 ${
                        isMissing 
                          ? 'text-red-400' 
                          : submitted?.status === 'approved' 
                            ? 'text-[#1ABC9C]' 
                            : 'text-yellow-400'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-[#F2F2F2]">{reqDoc.name}</p>
                      {submitted ? (
                        <div className="flex items-center gap-2 text-sm">
                          <Badge className={STATUS_STYLES[submitted.status]}>
                            {submitted.status}
                          </Badge>
                          {submitted.expires_at && (
                            <span className="text-[#A1A1AA]">
                              Expires: {format(parseISO(submitted.expires_at), 'dd/MM/yyyy')}
                            </span>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-red-400">Not uploaded</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {submitted && (
                      <>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDownload(submitted)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {submitted.status === 'pending' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteDocument(submitted.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </>
                    )}
                    {isMissing && (
                      <Button 
                        size="sm"
                        onClick={() => {
                          setSelectedType(reqDoc.id);
                          setExpiryDate(getDefaultExpiry(reqDoc.id));
                          setUploadDialogOpen(true);
                        }}
                        className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            {(!onboardingStatus?.required_documents || onboardingStatus.required_documents.length === 0) && (
              <div className="text-center py-8 text-[#A1A1AA]">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No document requirements configured</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* All Documents */}
      {documents.length > 0 && (
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardHeader>
            <CardTitle className="text-[#F2F2F2]">All Uploaded Documents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {documents.map(doc => (
                <div 
                  key={doc.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-[#1E1E1E]"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-[#A1A1AA]" />
                    <div>
                      <p className="font-medium text-[#F2F2F2]">{doc.file_name}</p>
                      <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                        <span>{doc.document_type_name}</span>
                        <span>•</span>
                        <span>{format(parseISO(doc.created_at), 'dd/MM/yyyy')}</span>
                        {doc.expires_at && (
                          <>
                            <span>•</span>
                            <span>Expires: {format(parseISO(doc.expires_at), 'dd/MM/yyyy')}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={STATUS_STYLES[doc.status]}>
                      {doc.status}
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDownload(doc)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    {doc.status === 'pending' && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteDocument(doc.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
